import React, { useState, useEffect } from "react";

// @material-ui/icons
import { Edit } from "components/MaterialSymbol/MaterialIcons";
import { Clear } from "components/MaterialSymbol/MaterialIcons";

// core components
import Button from "components/CustomButtons/Button.js";
import ReactTable from "components/ReactTable/ReactTable.js";
import Notification from "components/Notification/Notification.js";
import AreYouSure from "components/AreYouSure/AreYouSure";
import DeleteButton from "components/DeleteButton/DeleteButton";
import PageLayout from "layouts/PageLayout";
import TextActionButton from "components/TextActionButton/TextActionButton";

// @material-ui/core
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Chip
} from "@material-ui/core";

// components
import CreateEditShippingImportRule from "./CreateEditShippingImportRule";

// hooks
import { useConfirmationDialog } from "hooks/useConfirmationDialog";

// services
import ShippingImportRulesService from "../../services/ShippingImportRulesService";

export default function ShippingImportRules({ ...otherProps }) {
    const { openDialog, closeDialog } = useConfirmationDialog();
    const [rules, setRules] = useState([]);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState(null);

    // Filters state
    const [filterCountry, setFilterCountry] = useState("");
    const [filterRegime, setFilterRegime] = useState("");
    const [filterActive, setFilterActive] = useState("");

    const [notification, setNotification] = useState({
        open: false,
        color: "success",
        message: ""
    });

    const loadRules = async () => {
        try {
            const params = {};
            if (filterCountry) params.countryCode = filterCountry;
            if (filterRegime) params.regimeName = filterRegime;
            if (filterActive) params.isActive = filterActive;

            const res = await ShippingImportRulesService.getAll(params);
            setRules(res.data || []);
        } catch (error) {
            console.error("Error loading rules:", error);
            showNotification("danger", "Failed to retrieve shipping & import rules.");
        }
    };

    useEffect(() => {
        loadRules();
    }, [filterCountry, filterRegime, filterActive]);

    const showNotification = (color, message) => {
        setNotification({
            open: true,
            color: color,
            message: message
        });
        setTimeout(() => {
            setNotification((prev) => ({ ...prev, open: false }));
        }, 6000);
    };

    const handleCreateClick = () => {
        openDialog({
            customDialog: true,
            fullWidth: true,
            maxWidth: "md",
            message: (
                <CreateEditShippingImportRule
                    isEdit={false}
                    initialValues={{}}
                    onSuccess={() => {
                        loadRules();
                        showNotification("success", "Rule created successfully");
                        closeDialog();
                    }}
                    handleClose={closeDialog}
                />
            )
        });
    };

    const handleEditClick = (rule) => {
        openDialog({
            customDialog: true,
            fullWidth: true,
            maxWidth: "md",
            message: (
                <CreateEditShippingImportRule
                    isEdit={true}
                    initialValues={rule}
                    onSuccess={() => {
                        loadRules();
                        showNotification("success", "Rule updated successfully");
                        closeDialog();
                    }}
                    handleClose={closeDialog}
                />
            )
        });
    };

    const handleDeleteClick = (rule) => {
        setRuleToDelete(rule);
        setOpenDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!ruleToDelete) return;
        try {
            await ShippingImportRulesService.del(ruleToDelete.id);
            showNotification("success", "Rule deleted successfully");
            setRules((prev) => prev.filter((r) => r.id !== ruleToDelete.id));
        } catch (error) {
            console.error("Error deleting rule:", error);
            showNotification("danger", "Failed to delete shipping rule.");
        } finally {
            setOpenDeleteConfirm(false);
            setRuleToDelete(null);
        }
    };

    const handleClearFilters = () => {
        setFilterCountry("");
        setFilterRegime("");
        setFilterActive("");
    };

    const columns = [
        {
            Header: "ID",
            accessor: "id",
            width: 50
        },
        {
            Header: "Country",
            accessor: "countryCode",
            Cell: ({ value }) => (
                <Chip
                    label={value}
                    color="primary"
                    size="small"
                    style={{ fontWeight: "bold", background: "#3f51b5", color: "white" }}
                />
            )
        },
        {
            Header: "Regime",
            accessor: "regimeName"
        },
        {
            Header: "Currency",
            accessor: "currency"
        },
        {
            Header: "Shipping Rates",
            id: "shippingRates",
            Cell: ({ row: { original } }) => (
                <div style={{ fontSize: 13, lineHeight: "1.4" }}>
                    <div><strong>Base:</strong> {original.shippingBaseRate} / lb</div>
                    <div><strong>Volumetric:</strong> {original.shippingVolumetricRate} / lb</div>
                    <div><strong>Min Charge:</strong> {original.shippingMinCharge}</div>
                    <div><strong>Min Weight:</strong> {original.shippingMinWeight} lb</div>
                </div>
            )
        },
        {
            Header: "Limits",
            id: "limits",
            Cell: ({ row: { original } }) => (
                <div style={{ fontSize: 13, lineHeight: "1.4" }}>
                    <div><strong>Max Value:</strong> {original.maxMerchandiseValue}</div>
                    <div><strong>Max Weight:</strong> {original.maxWeightKg} kg</div>
                    <div><strong>Max Qty:</strong> {original.maxUnitsPerReference} units</div>
                </div>
            )
        },
        {
            Header: "Tax Exemption",
            accessor: "taxExemptionThreshold",
            Cell: ({ value }) => (
                <div style={{ fontSize: 13, textAlign: "center" }}>
                    {value > 0
                        ? <span>≤ <strong>${Number(value).toFixed(2)}</strong> exempt</span>
                        : <span style={{ color: "#999" }}>Always taxed</span>
                    }
                </div>
            )
        },
        {
            Header: "Taxes",
            id: "taxes",
            Cell: ({ row: { original } }) => (
                <div style={{ fontSize: 13, lineHeight: "1.4" }}>
                    <div><strong>Duty:</strong> {original.customsDutyPercentage}%</div>
                    <div><strong>VAT:</strong> {original.vatPercentage}%</div>
                </div>
            )
        },
        {
            Header: "Validity",
            id: "validity",
            Cell: ({ row: { original } }) => {
                const start = original.startDate ? original.startDate.split("T")[0] : "Anytime";
                const end = original.endDate ? original.endDate.split("T")[0] : "Always";
                return (
                    <div style={{ fontSize: 12 }}>
                        <div><strong>From:</strong> {start}</div>
                        <div><strong>To:</strong> {end}</div>
                    </div>
                );
            }
        },
        {
            Header: "Status",
            accessor: "isActive",
            Cell: ({ value }) => (
                <Chip
                    label={value === 1 ? "Active" : "Inactive"}
                    size="small"
                    style={{
                        background: value === 1 ? "#4caf50" : "#f44336",
                        color: "white",
                        fontWeight: "bold"
                    }}
                />
            )
        },
        {
            Header: "Actions",
            id: "actions",
            Cell: ({ row: { original } }) => (
                <div className="actions-right" style={{ display: "flex", gap: "4px" }}>
                    <Button
                        justIcon
                        round
                        simple
                        color="warning"
                        className="edit"
                        title="Edit"
                        onClick={() => handleEditClick(original)}
                    >
                        <Edit />
                    </Button>
                    <DeleteButton
                        title="shipping rule"
                        handleClick={() => handleDeleteClick(original)}
                    />
                </div>
            )
        }
    ];

    return (
        <PageLayout
            showSelectDealer={false}
            customComponent={
                <TextActionButton onClick={handleCreateClick}>
                    Create Rule
                </TextActionButton>
            }
            {...otherProps}
        >
            {notification.open && (
                <Notification
                    open={notification.open}
                    color={notification.color}
                    message={notification.message}
                />
            )}

            {/* Filters Row */}
            <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={16} mb={3}>
                <TextField
                    label="Country Code"
                    size="small"
                    placeholder="e.g. CO"
                    value={filterCountry}
                    onChange={(e) => setFilterCountry(e.target.value.toUpperCase())}
                    style={{ width: 110 }}
                />
                <TextField
                    label="Regime Name"
                    size="small"
                    placeholder="e.g. Courier"
                    value={filterRegime}
                    onChange={(e) => setFilterRegime(e.target.value)}
                    style={{ width: 150 }}
                />
                <FormControl style={{ minWidth: 120 }}>
                    <InputLabel id="active-filter-label">Status</InputLabel>
                    <Select
                        labelId="active-filter-label"
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                    >
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="true">Active Only</MenuItem>
                        <MenuItem value="false">Inactive Only</MenuItem>
                    </Select>
                </FormControl>
                {(filterCountry || filterRegime || filterActive) && (
                    <Button
                        color="transparent"
                        onClick={handleClearFilters}
                        justIcon
                        round
                        title="Clear Filters"
                    >
                        <Clear />
                    </Button>
                )}
            </Box>

            <ReactTable
                columns={columns}
                data={rules}
                showPagination={true}
                showSorting={true}
            />

            <AreYouSure
                open={openDeleteConfirm}
                title="Delete Shipping & Import Rule?"
                subtitle={`This rule for ${ruleToDelete?.countryCode} (${ruleToDelete?.regimeName}) will be permanently removed.`}
                onAccept={confirmDelete}
                onCancel={() => setOpenDeleteConfirm(false)}
            />
        </PageLayout>
    );
}
